/* eslint-disable camelcase */
import { useEffect, useState } from 'react';
import styles from './styles';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import { Grid, IconButton, Typography } from '@mui/material';
import { CancelPresentationRounded, DeleteRounded, SaveRounded } from '@mui/icons-material';
import columns from './table_config';
import PropTypes from 'prop-types';
import useUnsavedChanges from '../../../../../../hooks/useUnsavedChanges';
import { MAIN_DEPTH_POINTS, MAX_OBSERVATION_POINT_NAME_LENGTH, MAX_ROW_LIMITS, WELLS_OBSERVATION_POINTS } from '../../../../../../utils/constants';
import { generateRandom } from '../../../../../../utils/randomNumberGenerator';
import { enqueueSnackbar } from '../../../../../../store/actions/snackbar';
import SelectEditInputTextField from '../../../../../ui/SelectEditInputTextField';
import SelectNameAutoComplete from '../../../../../ui/HydraulicsModalDialog/SelectNameAutoComplete';
import ModalDialog from '../../../../../ui/ModalDialog';
import { COLORS } from '../../../../../../shared/config/colors';
import Datagrid from '../../../../../ui/Datagrid';
import DeleteConfirmationModal from '../../../../../deleteConfirmationModal';
import { saveObservationPoints } from '../../../../../../store/actions/hydraulicsLanding';

const LandingWellsObservationPointsModal = ({
  onCloseClick,
  isOpen,
  data,
  anchorPoint,
}) => {
  const classes = styles();
  const apiRef = useGridApiRef();
  const dispatch = useDispatch();
  const [properties, setProperties] = useState([]);
  const [invalidRows, setInvalidRows] = useState({});
  const [deleteAction, setDeleteAction] = useState(false);
  const [deleteRow, setDeleteRow] = useState();
  const observation_points = useSelector(state => state.hydraulicsLanding?.data?.observation_points);
  const observation_units = useSelector(state => state.hydraulicsLanding.units?.windows);
  const {
    unSavedExists,
    handleClose,
    UnsavedChangesModal
  } = useUnsavedChanges(properties, onCloseClick);
  const [namesSelection, setNamesSelection] = useState(WELLS_OBSERVATION_POINTS);

  const getUniqueNames = (arr = []) => {
    return [...new Set(arr.map(item => item?.name).filter(Boolean))];
  };

  useEffect(() => {
    if (observation_points?.length && isOpen) {
      setProperties(observation_points);
      setNamesSelection([...WELLS_OBSERVATION_POINTS, ...getUniqueNames(observation_points)]);
    } else {
      setProperties([]);
    }
  }, [isOpen]);

  const addNewObservationRow = () => () => {
    const newObservation = [...properties];
    const nextId = newObservation.length - 1;
    const newRow = {
      name: '',
      depth: '',
      grid_id: nextId + 1,
      _id: generateRandom(),
      isNewRow: true,
      isDirty: true
    };
    newObservation.unshift(newRow);
    setProperties(newObservation);
  };

  const handleAddObservation = () => {
    dispatch(addNewObservationRow());
  };

  useEffect(() => {
    if (properties[0]?.name === '') {
      const id = properties[0]._id;
      setTimeout(() => {
        apiRef.current.scrollToIndexes({
          rowIndex: 0
        });
        apiRef.current.setCellMode(id, 'name', 'edit');
        setTimeout(() => {
          apiRef.current.setCellFocus(id, 'name');
        }, 50);
      }, 50);
      apiRef.current.setPage(0);
    }
  }, [properties?.length]);

  const hasDuplicateDepthAndName = (arr, row) => {
    const seenMd = new Set();
    const seenNames = new Set();
    const seenCombinations = new Set();

    for (const item of arr) {
      const md = Number(item.md);
      const name = item.name?.toLowerCase()?.trim();
      const comboKey = `${md}-${name}`;

      if (seenCombinations.has(comboKey)) {
        dispatch(
          enqueueSnackbar(
            'Name and Measured Depth combination already exists',
            'error',
            new Date().getTime() + Math.random()
          )
        );
        setInvalidRows(prev => ({ ...prev, [row._id]: true }));
        return true;
      }

      if (seenMd.has(md)) {
        dispatch(
          enqueueSnackbar(
            'Measured Depth already exists',
            'error',
            new Date().getTime() + Math.random()
          )
        );
        setInvalidRows(prev => ({ ...prev, [row._id]: true }));
        return true;
      }

      if (seenNames.has(name)) {
        dispatch(
          enqueueSnackbar(
            'Name already exists',
            'error',
            new Date().getTime() + Math.random()
          )
        );
        setInvalidRows(prev => ({ ...prev, [row._id]: true }));
        return true;
      }

      seenMd.add(md);
      seenNames.add(name);
      seenCombinations.add(comboKey);
    }

    return false;
  };

  const checkObservationValue = (value) => {
    const row = value;
    const invalidFields = [];
    if (!row?.name) invalidFields.push('Name');
    if (row?.md === undefined || row?.md === null || row?.md === '') invalidFields.push('Measured depth');
    if (invalidFields.length === 0) {
      setInvalidRows(prev => ({ ...prev, [row._id]: false }));
      return true;
    } else {
      setInvalidRows(prev => ({ ...prev, [row._id]: true }));
      dispatch(
        enqueueSnackbar(
          `Please enter the following details: ${invalidFields.join(', ')}`,
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return false;
    }
  };

  const validateProperties = (row) => {
    const { name } = row;
    if (typeof name !== 'string' || name.length > MAX_OBSERVATION_POINT_NAME_LENGTH) {
      dispatch(
        enqueueSnackbar(
          `Name should be limited to ${MAX_OBSERVATION_POINT_NAME_LENGTH} characters`,
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return true;
    }
    if (Number(row?.md) < 0) {
      dispatch(
        enqueueSnackbar(
          `Measured Depth should be between 0.0 and ${data?.hole_depth}`,
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return true;
    }
    return false;
  };

  const handleSave = (row) => {
    if (checkObservationValue(row)) {
      if (!hasDuplicateDepthAndName(properties, row) && !validateProperties(row)) {
        const convertedMd = Number(row?.md);
        const isReservedName = MAIN_DEPTH_POINTS.some(
          point => point.toLowerCase() === row?.name?.toLowerCase()
        );

        const toTitleCase = (text) => text.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');

        const body = {
          grid_id: row.grid_id,
          _id: row._id,
          name: row?.name,
          md: convertedMd
        };

        if (isReservedName) {
          body.name = toTitleCase(row?.name);
        }
        setInvalidRows(prev => ({ ...prev, [row._id]: false }));
        const updated = properties.map(item => item._id === row._id ? body : item);
        setProperties(updated);
        dispatch(saveObservationPoints(updated));
      }
    }
  };

  const removeObservation = (row) => {
    if (row?.name === anchorPoint) {
      dispatch(
        enqueueSnackbar(
          'Deletion failed: The selected observation is currently set as the anchor point and cannot be removed.',
          'error',
          new Date().getTime() + Math.random()
        )
      );
    } else {
      const updatedObservation = properties.filter(item => item._id !== row?._id);
      setProperties(updatedObservation);
      dispatch(saveObservationPoints(updatedObservation));
    }
    setDeleteAction(false);
    setDeleteRow({});
  };

  const closeHandler = () => {
    if (unSavedExists) {
      handleClose(false);
      return;
    }
    onCloseClick();
  };

  const getModalTitle = () => {
    return (
      <Grid container alignItems="center" justifyContent="space-between">
        <Grid item>
          <Typography variant="h5">Well&apos;s Observation Points</Typography>
        </Grid>
        <Grid item>
          <IconButton
            onClick={closeHandler}
          >
            <CancelPresentationRounded fontSize='large' />
          </IconButton>
        </Grid>
      </Grid>
    );
  };

  const updateData = (id, field, value) => {
    setProperties(prevProperties => {
      const updatedProperties = prevProperties.map(item => {
        if (item._id === id) {
          const isDirty = value !== item[field];
          return {
            ...item,
            [field]: typeof value === 'string' ? value : Number(value),
            isDirty
          };
        }
        return item;
      });
      return updatedProperties;
    });
  };

  const renderValueCell = (params) => {
    return <SelectEditInputTextField numberFormatOnly {...params} updateData={updateData} />;
  };

  const renderActionCell = ({ row }) => {
    return (
      <Grid container justifyContent="center">
        <IconButton
          variant="solid"
          disabled={!row.isDirty}
          onClick={() => {
            setTimeout(() => {
              handleSave(row);
            }, 100);
          }}
        >
          <SaveRounded />
        </IconButton>
        <IconButton
          variant="solid"
          onClick={() => {
            setDeleteAction(true);
            setDeleteRow(row);
          }}
        >
          <DeleteRounded />
        </IconButton>
      </Grid>
    );
  };

  const renderNameEditCell = (params) => {
    return <SelectNameAutoComplete closeCellAfterSelect={true} {...params} data={namesSelection} updateData={updateData} maxLength={MAX_OBSERVATION_POINT_NAME_LENGTH} />;
  };

  return (
    <ModalDialog
      title={getModalTitle()}
      isOpen={isOpen}
      onClose={closeHandler}
      dialogClass={clsx(classes.dialogModal, classes.dialogMinWidth)}
    >
      <Grid container maxWidth='780px'>
        <Grid container>
          <Grid item marginLeft='12px' className={classes.customTool}>
            <Typography variant='button' fontWeight='700' onClick={handleAddObservation} className={properties?.length === MAX_ROW_LIMITS.WELLS_OBSERVATION || properties.some(item => item.isDirty === true) ? classes.disableIcon : ''}>+ Add</Typography>
          </Grid>
          <Grid item marginLeft='12px' className={classes.entryLabel}>
            <Typography variant='h6' color={COLORS.LIGHT_GREY} fontWeight='500'>{`(Note: Maximum ${MAX_ROW_LIMITS.WELLS_OBSERVATION} entries are allowed)`}</Typography>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Datagrid
            apiRef={apiRef}
            data={properties}
            sx={{
              marginLeft: '-6px',
              '.MuiDataGrid-cell': {
                fontSize: '1rem',
                fontWeight: 900
              },
              '.MuiDataGrid-columnHeader': {
                fontSize: '1.2rem',
                fontWeight: '900 !important'
              },
              '& .MuiDataGrid-footerContainer': {
                display: 'none',
                height: '0px'
              },
            }}
            columns={columns(renderNameEditCell, renderValueCell, renderActionCell, observation_units)}
            onRowClick={(params, evt) => {
              evt.preventDefault();
              evt.stopPropagation();
            }}
            disableStickyHeader={false}
            height={'30vh'}
            autoHeight={false}
            getRowId={(row) => row._id}
            getRowHeight={() => 'auto'}
            hideFooterPagination
            hideFooterSelectedRowCount
            getRowClassName={(params) => {
              return invalidRows[params.id] ? classes.invalidRow : '';
            }}
            pageSize={100}
            editMode="cell"
            toolbar={{
              options: {
                columns: false,
                filters: false,
                density: false,
                export: false
              },
            }}
            enableDirtyLock
          />
        </Grid>
      </Grid>
        {
            deleteAction && <DeleteConfirmationModal
                open={deleteAction}
                handleDelete={removeObservation}
                handleClose={setDeleteAction}
                deleteRow={deleteRow}
            />
        }
      <UnsavedChangesModal />
    </ModalDialog>
  );
};
LandingWellsObservationPointsModal.propTypes = {
  onCloseClick: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  data: PropTypes.any,
  anchorPoint: PropTypes.any,
};

export default LandingWellsObservationPointsModal;
